import { parseBoolean, parseNumber, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { RolodexEntryJson, RolodexEntryType } from './rolodexEntryFields'

/**
 * An entry which represents a section within a visual inspection form response.
 * Contains the section metadata and links to the parent form response.
 */
export class GeckoVisualInspectionSectionResponse extends GeckoEntryBase<
  {
    description: string | undefined
    section_id: string
    title: string
    order: number
    required: boolean
  },
  {
    organization: string
    site: string
    unit: string
    [RolodexEntryType.JOB]: string
    [RolodexEntryType.INSPECTION]: string
    [RolodexEntryType.VISUAL_INSPECTION]: string
    [RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE]: string
  },
  Record<string, never>
> {
  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        description: parseString(json.data.description),
        section_id: parseString(json.data.section_id) ?? '',
        title: parseString(json.data.title) ?? '',
        order: parseNumber(json.data.order) ?? 0,
        required: parseBoolean(json.data.required) ?? false,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        [RolodexEntryType.JOB]:
          parseString(json.tags[RolodexEntryType.JOB]) ?? '',
        [RolodexEntryType.INSPECTION]:
          parseString(json.tags[RolodexEntryType.INSPECTION]) ?? '',
        [RolodexEntryType.VISUAL_INSPECTION]:
          parseString(json.tags[RolodexEntryType.VISUAL_INSPECTION]) ?? '',
        [RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE]:
          parseString(
            json.tags[RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE]
          ) ?? '',
      },
      {}
    )
  }

  /**
   * A simple factory method which creates a visual inspection section response from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoVisualInspectionSectionResponse(json)
  }
}
